import { FinancialStatementPeriod } from 'common/types/financials';

// The year sequence a reader scans across, and so the only columns that anchor its range.
// LTM/NTM are pinned summary columns that always sort last — anchoring on them would make
// every real year interior and defeat the trim. Collapsed historical years are hidden by
// `Cell` (`isVisibleColumn`), so they have no width to reclaim and must not anchor either.
const isFiscalYearColumn = (column: FinancialStatementPeriod) => {
  if (column.isLTM || column.isNTM) {
    return false;
  }

  return column.isAdditionalHistoricalYear ? Boolean(column.showAdditionalHistoricalYear) : true;
};

// Removes LTM/NTM columns that carry no value. A blank LTM stays while NTM does: the NTM
// revenue growth rate is a relative expression against the column to its left, so dropping
// LTM would silently compare NTM against the last projection year instead.
const dropBlankSummaryColumns = (
  columns: FinancialStatementPeriod[],
  hasValue: (column: FinancialStatementPeriod) => boolean
) => {
  const keepsNTM = columns.some(column => column.isNTM && hasValue(column));

  return columns.filter(column => {
    if (column.isNTM) {
      return hasValue(column);
    }

    if (column.isLTM) {
      return keepsNTM || hasValue(column);
    }

    return true;
  });
};

// The historical/projection divider only separates something while a column follows it.
// Once trimming leaves the divider column last, its line would just be a stray right edge.
const dropTrailingDivider = (columns: FinancialStatementPeriod[]) => {
  const lastColumn = columns[columns.length - 1];

  if (!lastColumn?.isParentWithDivider) {
    return columns;
  }

  return [...columns.slice(0, -1), { ...lastColumn, isParentWithDivider: false }];
};

/**
 * Removes blank columns from the ends of a sheet's year sequence, never from inside it:
 * 2024 / blank 2025 / 2026 keeps 2025. Export only — on screen a blank year is an editable
 * placeholder, in a PDF it is dead width.
 *
 * `hasValue` decides whether a column carries anything. Every fiscal year column anchors the
 * populated range; `isTrimmable` narrows which of them a given sheet will actually remove.
 *
 * Blank LTM/NTM columns are removed too, on every sheet — a summary column with no financials
 * behind it has nothing to report. See `dropBlankSummaryColumns` for the one exception.
 */
const trimBlankExportColumns = ({
  columns,
  hasValue,
  isTrimmable,
}: {
  columns: FinancialStatementPeriod[];
  hasValue: (column: FinancialStatementPeriod) => boolean;
  isTrimmable: (column: FinancialStatementPeriod) => boolean;
}) => {
  const populatedIndexes = columns.reduce<number[]>((indexes, column, index) => {
    if (isFiscalYearColumn(column) && hasValue(column)) {
      indexes.push(index);
    }

    return indexes;
  }, []);

  // No fiscal year populated: trimming would leave only the pinned columns, so keep every year.
  if (!populatedIndexes.length) {
    return dropTrailingDivider(dropBlankSummaryColumns(columns, hasValue));
  }

  const firstPopulated = populatedIndexes[0];
  const lastPopulated = populatedIndexes[populatedIndexes.length - 1];

  const trimmedColumns = columns.filter((column, index) => {
    // Inside the populated run, blanks stay so no year goes missing from the sequence.
    if (index >= firstPopulated && index <= lastPopulated) {
      return true;
    }

    // Outside it, any fiscal year column is blank by construction — a populated one
    // would have widened the range to include it.
    return !(isFiscalYearColumn(column) && isTrimmable(column));
  });

  return dropTrailingDivider(dropBlankSummaryColumns(trimmedColumns, hasValue));
};

export default trimBlankExportColumns;
